import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Notebook, Sparkle, CircleNotch, CheckCircle, Clock, Target, Bookmark,
    ListChecks, GraduationCap, Warning, X, FloppyDisk, DownloadSimple, Broom,
} from "@phosphor-icons/react";

const LEVELS = [
    { key: "weak",    label: "Below average", desc: "Struggles with theory · needs extra revision" },
    { key: "average", label: "Average",       desc: "Needs strong scaffolding · frequent revision" },
    { key: "strong",  label: "Strong",        desc: "Absorbs quickly · needs stretch goals" },
];

const SAMPLE_SYLLABUS = "General Knowledge regarding current events; matter of everyday observation; broad outline of the Constitution of India; Indian History with special reference to Karnataka; Geography of India with special reference to Karnataka; matters relating to State and Regional Administration; development of Karnataka economy including initiatives relating to rural development, Panchayat Raj institutions, and rural cooperatives; environmental issues and development in Karnataka.";

const EXAM_OPTIONS = [
    { key: "", label: "—" }, { key: "kas", label: "KAS" }, { key: "vao", label: "VAO" }, { key: "group_c", label: "Group C" }, { key: "psi", label: "PSI" }, { key: "pc", label: "PC" },
];

export default function AdminStudyPlan() {
    const [form, setForm] = useState({
        title: "KPSC 100-hour Study Plan",
        exam_id: "",
        syllabus: SAMPLE_SYLLABUS,
        total_hours: 100,
        student_level: "average",
        graduation_percentage: 50,
        weekly_hours: 15,
        notes: "",
    });
    const [plan, setPlan] = useState(null);
    const [busy, setBusy] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const generate = async () => {
        if (!form.syllabus.trim()) { toast.error("Syllabus is required"); return; }
        if (!(form.total_hours > 0)) { toast.error("Total hours must be > 0"); return; }
        setBusy(true); setPlan(null); setError(null);
        try {
            const start = await api.post("/admin/study-plan/generate", form, { timeout: 60_000 });
            const jobId = start.data.job_id;
            if (!jobId) throw new Error("No job_id returned");
            // Poll every 3s up to 4 minutes
            const deadline = Date.now() + 4 * 60_000;
            while (Date.now() < deadline) {
                await new Promise((r) => setTimeout(r, 3000));
                const { data: job } = await api.get(`/admin/study-plan/jobs/${jobId}`);
                if (job.status === "done") { setPlan(job.result); setBusy(false); return; }
                if (job.status === "error") { const msg = job.error || "Study plan generation failed"; setError(msg); toast.error(msg); setBusy(false); return; }
            }
            setError("Study plan generation timed out after 4 minutes. Try smaller inputs.");
        } catch (e) {
            const msg = e.response?.data?.detail || e.message || "Study plan generation failed";
            setError(msg); toast.error(msg);
        } finally { setBusy(false); }
    };

    const savePlan = async () => {
        if (!plan) return;
        setSaving(true);
        try {
            await api.post("/admin/study-plan/save", { title: form.title || plan.title, exam_id: form.exam_id || null, plan });
            toast.success("Study plan saved");
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
        finally { setSaving(false); }
    };

    const exportTxt = () => {
        if (!plan) return;
        const lines = [];
        lines.push(`${plan.title || form.title}`);
        lines.push("=".repeat(60));
        lines.push(plan.summary || "");
        lines.push("");
        lines.push(`Total hours: ${plan.total_hours}    Weeks: ${plan.total_weeks}    Weekly: ${plan.weekly_hours}h`);
        lines.push("");
        lines.push("PHASES");
        (plan.phases || []).forEach((p, i) => {
            lines.push(`  ${i + 1}. ${p.name} — ${p.hours}h across ${p.weeks} week(s)`);
            lines.push(`     Focus: ${p.focus}`);
            (p.activities || []).forEach((a) => lines.push(`       • ${a}`));
        });
        lines.push("");
        lines.push("TOPIC ALLOCATION");
        (plan.topic_allocation || []).forEach((t) => lines.push(`  • ${t.topic} — ${t.hours}h (${t.weight_pct}%): ${t.why || ""}`));
        lines.push("");
        lines.push("WEEKLY SCHEDULE");
        (plan.weekly_schedule || []).forEach((w) => {
            lines.push(`  Week ${w.week}: ${w.focus} — ${w.daily_hours}h/day`);
            (w.targets || []).forEach((t) => lines.push(`    • ${t}`));
        });
        lines.push("");
        lines.push("RESOURCES");
        (plan.resources || []).forEach((r) => lines.push(`  • [${r.type}] ${r.name} (${r.when})`));
        lines.push("");
        lines.push("CHECKPOINTS");
        (plan.checkpoints || []).forEach((c) => lines.push(`  Week ${c.week}: ${c.checkpoint}`));
        lines.push("");
        lines.push("ADVICE FOR STUDENT");
        lines.push(plan.advice_for_student || "");

        const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${(plan.title || "study-plan").replace(/\s+/g, "-")}.txt`;
        a.click();
    };

    const clearAll = () => { setPlan(null); setError(null); };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-3 border-2 border-dashed border-[#BFD3FF] bg-blue-50/40 rounded-2xl p-5">
                <Notebook size={28} weight="duotone" className="text-[#1E3A8A] shrink-0 mt-0.5" />
                <div className="flex-1">
                    <h2 className="font-heading font-bold text-lg">Study Plan Generator</h2>
                    <p className="text-sm text-slate-600 mt-1">
                        Feed the syllabus, total hours and student profile — Claude Sonnet 4.5 will craft a phase-wise study plan calibrated to the aspirant&apos;s level (weak / average / strong).
                    </p>
                </div>
            </div>

            {error && (
                <div className="flex items-start gap-3 border-2 border-red-300 bg-red-50 rounded-2xl p-4">
                    <Warning size={22} weight="fill" className="text-red-600 shrink-0 mt-0.5" />
                    <div className="flex-1"><div className="font-heading font-bold text-sm text-red-900">Could not generate</div><p className="text-xs text-red-800 mt-1">{error}</p></div>
                    <button onClick={() => setError(null)} className="text-red-600 p-1"><X size={16} weight="bold" /></button>
                </div>
            )}

            {/* Input form */}
            <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl p-5 space-y-4">
                <div className="grid md:grid-cols-3 gap-3">
                    <input data-testid="sp-title" placeholder="Plan title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                    <select data-testid="sp-exam" value={form.exam_id} onChange={(e) => setForm({ ...form, exam_id: e.target.value })} className="px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm bg-white">
                        {EXAM_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label === "—" ? "No exam scope" : o.label}</option>)}
                    </select>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="text-xs">
                            <span className="text-slate-500">Total hours</span>
                            <input data-testid="sp-hours" type="number" min={1} value={form.total_hours} onChange={(e) => setForm({ ...form, total_hours: parseInt(e.target.value || "0") })} className="w-full mt-1 px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                        </label>
                        <label className="text-xs">
                            <span className="text-slate-500">Weekly hours</span>
                            <input type="number" min={1} value={form.weekly_hours} onChange={(e) => setForm({ ...form, weekly_hours: parseInt(e.target.value || "0") })} className="w-full mt-1 px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" />
                        </label>
                    </div>
                </div>

                <label className="block text-xs">
                    <span className="text-slate-500">Syllabus</span>
                    <textarea data-testid="sp-syllabus" value={form.syllabus} onChange={(e) => setForm({ ...form, syllabus: e.target.value })} rows={5} className="w-full mt-1 px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm resize-y" placeholder="Paste the exam syllabus here…" />
                </label>

                <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Student level</div>
                    <div className="flex flex-wrap gap-2">
                        {LEVELS.map((L) => (
                            <button
                                key={L.key}
                                data-testid={`sp-level-${L.key}`}
                                onClick={() => setForm({ ...form, student_level: L.key })}
                                className={`text-left px-3 py-2 rounded-lg border-2 text-xs font-semibold transition-colors flex-1 min-w-[180px] ${
                                    form.student_level === L.key ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-700 border-[#BFD3FF] hover:border-[#1E3A8A]"
                                }`}
                            >
                                <div>{L.label}</div>
                                <div className={`text-[10px] mt-0.5 font-normal ${form.student_level === L.key ? "opacity-80" : "text-slate-500"}`}>{L.desc}</div>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-3">
                    <label className="text-xs">
                        <span className="text-slate-500">Graduation percentage</span>
                        <input type="number" step="0.1" min={0} max={100} value={form.graduation_percentage ?? ""} onChange={(e) => setForm({ ...form, graduation_percentage: e.target.value ? parseFloat(e.target.value) : null })} className="w-full mt-1 px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" placeholder="e.g. 50" />
                    </label>
                    <label className="text-xs">
                        <span className="text-slate-500">Notes (optional)</span>
                        <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full mt-1 px-3 py-2 border-2 border-[#BFD3FF] rounded-lg text-sm" placeholder="Weak in polity, prefers video…" />
                    </label>
                </div>

                <div className="flex items-center gap-3 pt-2">
                    {plan && <button onClick={clearAll} className="btn-ghost !py-2 !px-4"><Broom size={16} weight="bold" /> Clear plan</button>}
                    <button
                        data-testid="sp-generate"
                        onClick={generate}
                        disabled={busy || !form.syllabus.trim() || !(form.total_hours > 0)}
                        className="ml-auto btn-primary !py-2 !px-5 disabled:opacity-50"
                    >
                        {busy ? <><CircleNotch size={16} className="animate-spin" /> Generating (60-90s)…</> : <><Sparkle size={16} weight="fill" /> Generate plan</>}
                    </button>
                </div>
            </div>

            {/* Plan output */}
            {plan && (
                <div data-testid="sp-plan" className="space-y-5">
                    <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl p-6">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="flex-1">
                                <h2 className="font-heading font-black text-2xl">{plan.title}</h2>
                                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{plan.summary}</p>
                            </div>
                            <div className="flex flex-wrap gap-3 text-center">
                                {[
                                    { icon: Clock, label: "Total hours", v: plan.total_hours },
                                    { icon: Bookmark, label: "Weeks", v: plan.total_weeks },
                                    { icon: Target, label: "Hours/wk", v: plan.weekly_hours },
                                ].map((s) => (
                                    <div key={s.label} className="border-2 border-[#BFD3FF] rounded-xl px-3 py-2 min-w-[80px]">
                                        <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold text-slate-500"><s.icon size={11} weight="duotone" /> {s.label}</div>
                                        <div className="font-heading font-black text-xl tabular-nums">{s.v}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="mt-5 flex flex-wrap gap-2">
                            <button data-testid="sp-save" onClick={savePlan} disabled={saving} className="btn-primary !py-2 !px-4 disabled:opacity-50">
                                {saving ? <CircleNotch size={14} className="animate-spin" /> : <FloppyDisk size={14} weight="bold" />} Save plan
                            </button>
                            <button onClick={exportTxt} className="btn-ghost !py-2 !px-4"><DownloadSimple size={14} weight="bold" /> Export .txt</button>
                        </div>
                    </div>

                    {/* Phases */}
                    {plan.phases?.length > 0 && (
                        <Card title="Phases" icon={ListChecks}>
                            <div className="grid md:grid-cols-2 gap-3">
                                {plan.phases.map((ph, i) => (
                                    <div key={i} className="border-2 border-[#BFD3FF] rounded-xl p-4">
                                        <div className="flex items-baseline justify-between">
                                            <div className="font-heading font-bold text-sm text-[#1E3A8A]">{i + 1}. {ph.name}</div>
                                            <div className="text-[11px] text-slate-500 font-semibold">{ph.hours}h · {ph.weeks}w</div>
                                        </div>
                                        <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{ph.focus}</p>
                                        <ul className="mt-2 space-y-0.5">
                                            {(ph.activities || []).map((a, j) => (
                                                <li key={j} className="text-[11px] text-slate-700 flex items-start gap-1.5"><CheckCircle size={11} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" /> {a}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}

                    {/* Topic Allocation */}
                    {plan.topic_allocation?.length > 0 && (
                        <Card title="Topic Allocation" icon={GraduationCap}>
                            <div className="space-y-2">
                                {plan.topic_allocation.map((t, i) => (
                                    <div key={i}>
                                        <div className="flex items-baseline justify-between text-xs">
                                            <span className="font-semibold text-slate-800 truncate">{t.topic}</span>
                                            <span className="text-slate-500 shrink-0 ml-3">{t.hours}h · {t.weight_pct}%</span>
                                        </div>
                                        <div className="h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
                                            <div className="h-full bg-[#1E3A8A]" style={{ width: `${Math.min(100, t.weight_pct || 0)}%` }} />
                                        </div>
                                        {t.why && <div className="text-[10px] text-slate-500 mt-0.5">{t.why}</div>}
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}

                    {/* Weekly Schedule */}
                    {plan.weekly_schedule?.length > 0 && (
                        <Card title="Weekly Schedule" icon={Bookmark}>
                            <div className="max-h-96 overflow-auto pr-1">
                                <table className="w-full text-xs">
                                    <thead className="bg-slate-50 sticky top-0">
                                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                                            <th className="px-2 py-1.5 w-16">Week</th>
                                            <th className="px-2 py-1.5">Focus & targets</th>
                                            <th className="px-2 py-1.5 w-16 text-right">h/day</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {plan.weekly_schedule.map((w, i) => (
                                            <tr key={i} className="border-t border-slate-100">
                                                <td className="px-2 py-2 font-bold text-[#1E3A8A]">{w.week}</td>
                                                <td className="px-2 py-2">
                                                    <div className="font-medium">{w.focus}</div>
                                                    <ul className="mt-1 space-y-0.5">
                                                        {(w.targets || []).map((t, j) => <li key={j} className="text-[11px] text-slate-600 pl-3 relative before:content-['·'] before:absolute before:left-0">{t}</li>)}
                                                    </ul>
                                                </td>
                                                <td className="px-2 py-2 text-right tabular-nums">{w.daily_hours}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    )}

                    {/* Resources + Checkpoints */}
                    <div className="grid md:grid-cols-2 gap-4">
                        {plan.resources?.length > 0 && (
                            <Card title="Resources" icon={Bookmark}>
                                <ul className="space-y-1.5">
                                    {plan.resources.map((r, i) => (
                                        <li key={i} className="text-xs flex items-start gap-2">
                                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 uppercase shrink-0">{r.type}</span>
                                            <span><span className="font-medium">{r.name}</span> <span className="text-slate-400">— {r.when}</span></span>
                                        </li>
                                    ))}
                                </ul>
                            </Card>
                        )}
                        {plan.checkpoints?.length > 0 && (
                            <Card title="Checkpoints" icon={CheckCircle}>
                                <ul className="space-y-1.5">
                                    {plan.checkpoints.map((c, i) => (
                                        <li key={i} className="text-xs flex items-start gap-2">
                                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#1E3A8A]/10 text-[#1E3A8A] shrink-0">Week {c.week}</span>
                                            <span>{c.checkpoint}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Card>
                        )}
                    </div>

                    {/* Advice */}
                    {plan.advice_for_student && (
                        <Card title="Advice for the Student" icon={Sparkle}>
                            <p className="text-sm text-slate-700 leading-relaxed italic">{plan.advice_for_student}</p>
                        </Card>
                    )}
                </div>
            )}
        </div>
    );
}

function Card({ title, icon: Icon, children }) {
    return (
        <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl overflow-hidden">
            <div className="px-4 py-2 border-b-2 border-[#BFD3FF] text-[10px] uppercase tracking-widest font-bold text-slate-600 flex items-center gap-1.5">
                <Icon size={13} weight="duotone" /> {title}
            </div>
            <div className="p-4">{children}</div>
        </div>
    );
}
